import React, { useMemo } from 'react';
import CalendarRow from './calendarRow';
import {
  PraksisgruppeDto,
  PraksiskalenderAktivitetDto,
  PraksiskalenderInstitusjonerDto,
  PraksisklasseDto,
} from '../api/generated/praksissted-generated-api';
import { groupBy } from '../util';

type KalenderAktiviteterByKlasseId = {
  [klasseId: string]: PraksiskalenderAktivitetDto[];
};

interface PraksisgrupperProps {
  praksisgruppe: PraksisgruppeDto;
  filteredAktiviteterByKlasseId: KalenderAktiviteterByKlasseId;
}

const PraksisgrupperPerInstitusjon = React.memo<PraksisgrupperProps>(
  ({ praksisgruppe, filteredAktiviteterByKlasseId }) => (
    <>
      {praksisgruppe.institusjoner.map(
        (institusjon: PraksiskalenderInstitusjonerDto, i: number) => (
          <Institusjon
            key={`${institusjon.institusjonsnavn}-${i}`}
            firstInstitusjon={i === 0}
            institusjon={institusjon}
            praksistypenavn={praksisgruppe.praksis.navn}
            filteredAktiviteterByKlasseId={filteredAktiviteterByKlasseId}
          />
        ),
      )}
    </>
  ),
);

type KlassenavnEmnekode = `${string}|${string}`;

const getKlassenavnEmnekode = (klasse: PraksisklasseDto): KlassenavnEmnekode =>
  `${klasse.presentertNavn || klasse.bokstav}|${klasse.emne.kode}`;

const getKlassenavn = (klassenavnEmnekode: KlassenavnEmnekode): string =>
  klassenavnEmnekode.split('|')[0];

const getAktiviteterByKlassenavn = (
  klasser: PraksisklasseDto[],
  practiceActivitiesFiltered: KalenderAktiviteterByKlasseId,
): { [klassenavn: string]: PraksiskalenderAktivitetDto[] } =>
  klasser.reduce(
    (
      acc: { [klassenavn: KlassenavnEmnekode]: PraksiskalenderAktivitetDto[] },
      klasse: PraksisklasseDto,
    ) => {
      const klassenavn = getKlassenavnEmnekode(klasse);
      const prevItems = acc[klassenavn] || [];

      return {
        ...acc,
        [klassenavn]: [...prevItems, ...practiceActivitiesFiltered[klasse.id]],
      };
    },
    {},
  );

interface InstitusjonProps {
  institusjon: PraksiskalenderInstitusjonerDto;
  firstInstitusjon: boolean;
  praksistypenavn: string;
  filteredAktiviteterByKlasseId: KalenderAktiviteterByKlasseId;
}

const Institusjon: React.FC<React.PropsWithChildren<InstitusjonProps>> = ({
  institusjon,
  firstInstitusjon,
  praksistypenavn,
  filteredAktiviteterByKlasseId,
}) => {
  const { klasser } = institusjon;

  const klasserByKlassenavnEmnekode = useMemo(
    () => groupBy(klasser, getKlassenavnEmnekode),
    [klasser],
  );

  const aktiviteterByKlassenavn = useMemo(
    () => getAktiviteterByKlassenavn(klasser, filteredAktiviteterByKlasseId),
    [klasser, filteredAktiviteterByKlasseId],
  );

  return (
    <>
      {Object.entries(klasserByKlassenavnEmnekode).map(
        ([klassenavnEmnekode, klasserOnNavn], j: number) => {
          if (klasserOnNavn.length === 0) return <></>;

          const shouldRenderInstitustionHeader = j === 0;
          const shouldRenderPraksistypeHeader =
            firstInstitusjon && shouldRenderInstitustionHeader;

          const firstKlasseWithNavn = klasserOnNavn[0];
          const { emne } = firstKlasseWithNavn;

          return (
            <CalendarRow
              key={firstKlasseWithNavn.id}
              shouldRenderInstitustionHeader={shouldRenderInstitustionHeader}
              shouldRenderPracticeTypeHeader={shouldRenderPraksistypeHeader}
              praksistypenavn={praksistypenavn}
              institusjon={institusjon}
              klassenavn={getKlassenavn(
                klassenavnEmnekode as KlassenavnEmnekode,
              )}
              emne={emne}
              aktiviteter={aktiviteterByKlassenavn[klassenavnEmnekode]}
            />
          );
        },
      )}
    </>
  );
};

export default PraksisgrupperPerInstitusjon;
